import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import type { updateNavigationCustomisationMutation } from '@atlassian/jira-relay/src/__generated__/updateNavigationCustomisationMutation.graphql';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { MutationError } from '@atlassian/jira-mutation-error/src/index.tsx';
import { useFlagsService, type FlagConfiguration } from '@atlassian/jira-flags';
import type { ViewOptionsType } from '../ui/change-view-menu-item/view-options/types.tsx';
import type { DisplaySettingsProperties } from '../ui/display-settings-provider/types.tsx';
import { DisplaySettingsPropertyKeys } from '../ui/display-settings-provider/constants.tsx';
import type { NavigationCustomisationNodes } from './types.tsx';
import { applyNavigationCustomisation } from './navigation-customisation-properties.tsx';
import messages from './messages.tsx';

const UPDATE_NAVIGATION_CUSTOMISATION_ERROR_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.defaultErrorTitle,
	description: messages.defaultErrorMessage,
};

export const useUpdateNavigationCustomisation = (
	id: string | undefined,
	nodes: NavigationCustomisationNodes,
	entityAri: string,
	ownerAri: string,
) => {
	const { showFlag } = useFlagsService();

	const [commitMutation] = useMutation<updateNavigationCustomisationMutation>(graphql`
		mutation updateNavigationCustomisationMutation($input: SettingsNavigationCustomisationInput!) {
			settings_updateNavigationCustomisation(input: $input) {
				properties {
					nodes {
						key
						value
					}
				}
			}
		}
	`);

	const handleError = useCallback(
		(error: MutationError) => {
			fireErrorAnalytics({
				meta: {
					id: 'updateNavigationCustomisation',
					packageName: 'jiraNavigationAppsSidebarNav4DisplaySettings',
					teamName: 'jira-cosmos',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
			showFlag(UPDATE_NAVIGATION_CUSTOMISATION_ERROR_FLAG);
		},
		[showFlag],
	);

	const updateNavigationCustomisation = useCallback(
		(displaySettingProperties: DisplaySettingsProperties) => {
			const properties = applyNavigationCustomisation(nodes, displaySettingProperties);
			commitMutation({
				variables: {
					input: {
						entityAri,
						ownerAri,
						properties,
					},
				},
				optimisticResponse: {
					settings_updateNavigationCustomisation: {
						id,
						properties: {
							nodes: properties,
						},
					},
				},
				onCompleted: (_, errors) => {
					if (errors && errors.length > 0) {
						const error = new MutationError(errors[0]);
						handleError(error);
					}
				},
				onError: (error) => {
					handleError(new MutationError(error));
				},
			});
		},
		[commitMutation, id, nodes, entityAri, ownerAri, handleError],
	);

	const setSelectedViewOption = (viewOption: ViewOptionsType) => {
		updateNavigationCustomisation({ [DisplaySettingsPropertyKeys.VIEW_OPTION]: viewOption });
	};

	return { setSelectedViewOption };
};
